import React, { useCallback, useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import "./CompletionSeal.css";

export const PILLARS = ["Instruction", "Administrative Leadership", "Fiscal", "Governance", "Compliance", "Student Performance", "School Management", "Mission and Key Design Elements"];
const STEP_MS = 450;
const HOLD_MS = 6000;
const reduced = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const arc = (i) => {
  const r = 54, gap = 1.2, a0 = (i * 45 + gap) * Math.PI / 180, a1 = ((i + 1) * 45 - gap) * Math.PI / 180;
  return `M ${60 + r * Math.cos(a0)} ${60 + r * Math.sin(a0)} A ${r} ${r} 0 0 1 ${60 + r * Math.cos(a1)} ${60 + r * Math.sin(a1)}`;
};

const Seg = ({ i, step }) => (
  <>
    <path d={arc(i)} className="cseal-track" />
    <path d={arc(i)} pathLength="1" className={`cseal-seg ${i < step ? "on" : ""}`} />
  </>
);

// Segments are written out (no .map) so dev tooling never injects HTML wrappers into the SVG.
const Ring = ({ step, onClick, period, date }) => (
  <div className="cseal-ring" onClick={onClick} aria-hidden="true" data-testid="completion-seal-ring">
    <svg viewBox="0 0 120 120">
      <Seg i={0} step={step} /><Seg i={1} step={step} /><Seg i={2} step={step} /><Seg i={3} step={step} />
      <Seg i={4} step={step} /><Seg i={5} step={step} /><Seg i={6} step={step} /><Seg i={7} step={step} />
    </svg>
    <div className="cseal-disc" data-testid="completion-seal-disc">
      <Check className="h-7 w-7" strokeWidth={3} />
      <span className="cseal-label">DIAGNOSTIC<br />COMPLETE</span>
      <span className="cseal-period">{period}</span>
      <span className="cseal-date">{date}</span>
    </div>
  </div>
);

export const CompletionSeal = ({ data, onResults, onDashboard }) => {
  const motionOff = useRef(reduced()).current;
  const [step, setStep] = useState(motionOff ? 8 : 0);
  const [final, setFinal] = useState(motionOff);
  const interacted = useRef(motionOff);
  const programmatic = useRef(false);
  const skipRef = useRef(null);
  const viewRef = useRef(null);

  const jump = useCallback(() => { interacted.current = true; setStep(8); setFinal(true); }, []);

  useEffect(() => { window.scrollTo(0, 0); if (!motionOff) skipRef.current?.focus({ preventScroll: true }); }, [motionOff]);
  useEffect(() => {
    if (final) return undefined;
    if (step >= 8) { const t = setTimeout(() => setFinal(true), STEP_MS); return () => clearTimeout(t); }
    const t = setTimeout(() => setStep((s) => s + 1), step === 0 ? 150 : STEP_MS);
    return () => clearTimeout(t);
  }, [step, final]);
  useEffect(() => {
    if (!final) return undefined;
    programmatic.current = true; viewRef.current?.focus({ preventScroll: true }); programmatic.current = false;
    if (interacted.current) return undefined;
    const t = setTimeout(() => { if (!interacted.current) onResults(); }, HOLD_MS);
    return () => clearTimeout(t);
  }, [final, onResults]);
  useEffect(() => {
    const stop = () => { interacted.current = true; };
    const onKey = (e) => { stop(); if (e.key === "Escape") jump(); };
    const onFocus = () => { if (!programmatic.current) stop(); };
    window.addEventListener("keydown", onKey); window.addEventListener("pointerdown", stop); document.addEventListener("focusin", onFocus);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("pointerdown", stop); document.removeEventListener("focusin", onFocus); };
  }, [jump]);

  const date = data.completed_at ? new Date(data.completed_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";
  const period = data.period_label || "";
  const line = data.indicator_count
    ? `All 8 pillars and ${data.indicator_count} indicators submitted on ${date}. Your results are ready.`
    : `All 8 pillars submitted on ${date}. Your results are ready.`;

  return (
    <section className={`cseal ${final ? "final" : ""}`} data-testid="completion-seal" aria-labelledby="cseal-heading">
      {!final && <button ref={skipRef} type="button" className="cseal-skip" onClick={jump} data-testid="completion-seal-skip">Skip</button>}
      <div className="cseal-sr" aria-live="polite" data-testid="completion-seal-announce">{final ? `Diagnostic complete. ${period}. All 8 pillars submitted.` : ""}</div>
      <Ring step={step} onClick={jump} period={period} date={date} />
      <div className="cseal-chips" aria-hidden="true" data-testid="completion-seal-chips">
        {PILLARS.slice(0, step).map((p) => <span key={p} className="cseal-chip"><Check className="h-3.5 w-3.5" />{p}</span>)}
      </div>
      <div className="cseal-after">
        <h1 id="cseal-heading" className="cseal-h" data-testid="completion-seal-heading">{data.school_name} has completed the {period} MySchoolRx diagnostic</h1>
        <p className="cseal-line" data-testid="completion-seal-line">{line}</p>
        <div className="cseal-btns">
          <button ref={viewRef} type="button" className="cseal-btn primary" onClick={onResults} tabIndex={final ? 0 : -1} data-testid="completion-seal-view-results">View your results</button>
          <button type="button" className="cseal-btn secondary" onClick={onDashboard} tabIndex={final ? 0 : -1} data-testid="completion-seal-dashboard">Back to dashboard</button>
        </div>
      </div>
    </section>
  );
};
